import { CartShipToHomeType } from '@adapters/bigcommerce/types/cart';
import { transformShippingPrice } from '@utils/transformShippingPrice';
import shipperhqRates from '../../shipperhq-rates.json';
import { ShipperHQRates } from '@adapters/bigcommerce/types/shipping';

const shipperhqRatesTyped = (shipperhqRates as unknown as ShipperHQRates) || {};

// Format dates based on locale
export const formatDate = (date: Date, locale: string) => {
  return date.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    weekday: locale === 'fr' ? 'long' : 'short',
    month: locale === 'fr' ? 'long' : 'short',
    day: 'numeric',
  });
};

const getStartBusinessDays = (cartShipToHomeType: CartShipToHomeType) => {
  if (cartShipToHomeType === CartShipToHomeType.BIKE_ONLY) {
    return shipperhqRatesTyped?.allRates?.bike_only?.shippingDays - 1;
  }
  if (cartShipToHomeType === CartShipToHomeType.EXPRESS) {
    return shipperhqRatesTyped?.allRates?.express?.shippingDays;
  }
  return shipperhqRatesTyped?.allRates?.regular?.shippingDays - 1;
};

const getEndBusinessDays = (cartShipToHomeType: CartShipToHomeType) => {
  if (
    cartShipToHomeType === CartShipToHomeType.BIKE_ONLY ||
    cartShipToHomeType === CartShipToHomeType.MULTICARRIER
  ) {
    return shipperhqRatesTyped?.allRates?.bike_only?.shippingDays + 1;
  }
  if (cartShipToHomeType === CartShipToHomeType.EXPRESS) {
    return shipperhqRatesTyped?.allRates?.express?.shippingDays + 1;
  }
  return shipperhqRatesTyped?.allRates?.regular?.shippingDays + 1;
};

export const getDeliveryTime = (
  cartShipToHomeType: CartShipToHomeType,
  locale?: string
) => {
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  const startBusinessDays = getStartBusinessDays(cartShipToHomeType) || 5;
  const endBusinessDays = getEndBusinessDays(cartShipToHomeType) || 7;

  // Add 5 business days to start date
  let businessDaysAdded = 0;
  while (businessDaysAdded < startBusinessDays) {
    startDate.setDate(startDate.getDate() + 1);
    if (startDate.getDay() !== 0 && startDate.getDay() !== 6) {
      businessDaysAdded += 1;
    }
  }

  // Add 7 business days to end date
  businessDaysAdded = 0;
  while (businessDaysAdded < endBusinessDays) {
    endDate.setDate(endDate.getDate() + 1);
    if (endDate.getDay() !== 0 && endDate.getDay() !== 6) {
      businessDaysAdded += 1;
    }
  }

  return locale === 'fr'
    ? `${formatDate(startDate, locale || 'fr')} et le ${formatDate(
        endDate,
        locale || 'fr'
      )}`
    : `${formatDate(startDate, locale || 'en')} – ${formatDate(
        endDate,
        locale || 'en'
      )}`;
};

export const getExtendedDeliveryTime = (shippingDays: number) => {
  const startBusinessDays = shippingDays - 1;
  const endBusinessDays = shippingDays + 1;

  return `${startBusinessDays}-${endBusinessDays}`;
};

export const getEstimatedDeliveryDate = (
  businessDaysRange?: string,
  locale?: string
): string => {
  // Parse the date string (e.g., "Aug 28, 2025")
  const baseDate = new Date();
  if (isNaN(baseDate.getTime())) {
    throw new Error('Invalid date string provided');
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day

  // If businessDaysRange is provided, use it to calculate the range
  // Otherwise, calculate business days from today to the input date
  let startBusinessDays = 5; // default
  let endBusinessDays = 7; // default

  if (businessDaysRange) {
    const rangeMatch = businessDaysRange.match(/(\d+)-(\d+)/);
    if (rangeMatch) {
      startBusinessDays = parseInt(rangeMatch[1]);

      endBusinessDays = parseInt(rangeMatch[2]);
    }
  } else {
    // Calculate business days from today to the input date
    baseDate.setHours(0, 0, 0, 0); // Reset time to start of day
    let businessDaysFromToday = 0;
    const currentDate = new Date(today);
    while (currentDate < baseDate) {
      currentDate.setDate(currentDate.getDate() + 1);
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        businessDaysFromToday += 1;
      }
    }

    startBusinessDays = businessDaysFromToday - 1;

    endBusinessDays = businessDaysFromToday + 1;
  }

  // Calculate the actual dates based on business days from today
  const startDate = new Date(today);
  const endDate = new Date(today);

  // Add start business days
  let businessDaysAdded = 0;
  while (businessDaysAdded < startBusinessDays) {
    startDate.setDate(startDate.getDate() + 1);
    if (startDate.getDay() !== 0 && startDate.getDay() !== 6) {
      businessDaysAdded += 1;
    }
  }

  // Add end business days
  businessDaysAdded = 0;
  while (businessDaysAdded < endBusinessDays) {
    endDate.setDate(endDate.getDate() + 1);
    if (endDate.getDay() !== 0 && endDate.getDay() !== 6) {
      businessDaysAdded += 1;
    }
  }

  return locale === 'fr'
    ? `${formatDate(startDate, locale || 'fr')} et le ${formatDate(
        endDate,
        locale || 'fr'
      )}`
    : `${formatDate(startDate, locale || 'en')} – ${formatDate(
        endDate,
        locale || 'en'
      )}`;
};

export const getBusinessDaysRange = (
  cartShipToHomeType: CartShipToHomeType
) => {
  const startBusinessDays = getStartBusinessDays(cartShipToHomeType) || 5;
  const endBusinessDays = getEndBusinessDays(cartShipToHomeType) || 7;
  return `${startBusinessDays}-${endBusinessDays}`;
};

export const getShippingAmount = (
  cartTotal?: number,
  locale?: string,
  cartShipToHomeType?: CartShipToHomeType
) => {
  const freeShippingThreshold = process.env
    .NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_AMOUNT
    ? Number(process.env.NEXT_PUBLIC_FREE_SHIPPING_THRESHOLD_AMOUNT)
    : 50;

  const isFreeShipping = cartTotal && cartTotal >= freeShippingThreshold;

  const regularShippingAmount = isFreeShipping
    ? 0
    : shipperhqRatesTyped?.allRates?.regular?.cost;

  if (cartShipToHomeType === CartShipToHomeType.OVER_SIZE_SURCHARGE) {
    return `${transformShippingPrice(
      shipperhqRatesTyped?.allRates?.over_size_surcharge?.cost,
      locale
    )}`;
  }

  if (
    cartShipToHomeType === CartShipToHomeType.BIKE_ONLY ||
    cartShipToHomeType === CartShipToHomeType.MULTICARRIER
  ) {
    return `${transformShippingPrice(
      shipperhqRatesTyped?.allRates?.bike_only?.cost,
      locale
    )}`;
  }

  return `${transformShippingPrice(regularShippingAmount, locale)}`;
};
